import React from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { SelectChangeEvent } from "@mui/material/Select";
import { Controller, useForm } from "react-hook-form";

import { Modal } from "../Modal";
import { MUISelect } from "../MUISelect";
import { useModalContext } from "../../contexts/ModalContext/context";
import {
  createIntegrationsMappings,
  createIntegrationsKinetic,
  updateIntegrationsKinetic,
  IIntegrationsMappings,
  IIntegrationsKinetic,
  updateIntegrationsMappings,
} from "../../services/IntegrationApi";
import useAuth from "../../routes/login/AuthContext";
import { useSelectedNetworkContext } from "../../contexts/NetworkContext/context";
import { useIntegrationContext } from "../IntegrationTable/IntegrationProvider";
import { normalize } from "../../helpers/normalize";
import { useSnackbar } from "../../hooks/useSnackbar";
import { useNetwork } from "../../hooks/useNetwork";
import { Button } from "../../UI/MUIButton";
import { extractErrorMessage } from "../../helpers/extractErrorMessage";
import { L3Selector, L2Selector, L4Selector } from "../MUINetworkSelectors";

interface IFormData {
  access: "enabled" | "disabled";
  mappingValue: string;
  externalId: string;
  seasonId: string;
  grouping: string;
  networkMappingType: string;
  // Kinetic-specific fields
  instanceId: string;
  yearOfStudy: string;
  studentType: string;
  pathwayId: string;
}

interface IProps {
  defaultProps?: IIntegrationsMappings | IIntegrationsKinetic;
  isKinetic?: boolean; // Simple prop to control Kinetic mode - can be replaced with detection logic later
}

type MappingFieldsOption = "BOTH" | "STUDENT_TYPE" | "PATHWAY_ID" | "NONE";

export const MappingFormModal: React.FC<IProps> = (props) => {
  const { loadIntegrations } = useIntegrationContext();
  const { selectedNetworkId } = useSelectedNetworkContext();
  const { control, handleSubmit, reset, formState: { errors } } = useForm<IFormData>();
  const [unitType, setUnitType] = React.useState<number>(0);
  const [roomType, setRoomType] = React.useState<number | undefined>();
  const [isLoading, setIsLoading] = React.useState(false);
  const { setIsOpen } = useModalContext();
  const { getAuthToken } = useAuth();
  const { getL4Options } = useNetwork();
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();
  const [subnetwork, setSubnetwork] = React.useState<number>(0);
  const [mappingFields, setMappingFields] = React.useState<MappingFieldsOption>("BOTH");

  React.useEffect(() => {
    if (!props.defaultProps) return;

    setSubnetwork(props.defaultProps.l2Id);
    setUnitType(props.defaultProps.l3Id || 0);

    if (props.isKinetic) {
      // Reset with Kinetic-specific fields
      const kineticProps = props.defaultProps as IIntegrationsKinetic;
      setMappingFields(kineticProps.importFieldSelection || "BOTH");
      reset({
        access: kineticProps.enableAccess ? "enabled" : "disabled",
        mappingValue: kineticProps.instanceId || "",
        networkMappingType: kineticProps.networkMappingType,
        externalId: "",
        seasonId: kineticProps.yearOfStudyId || "",
        grouping: String(kineticProps.pathwayId || ""),
        instanceId: kineticProps.instanceId || "",
        yearOfStudy: kineticProps.yearOfStudyId || "",
        studentType: kineticProps.studentType || "",
        pathwayId: String(kineticProps.pathwayId || ""),
      });
    } else {
      // Reset with standard fields
      const standardProps = props.defaultProps as IIntegrationsMappings;
      reset({
        access: standardProps.enableAccess ? "enabled" : "disabled",
        mappingValue: standardProps.mapping,
        networkMappingType: standardProps.networkMappingType,
        externalId: String(standardProps.externalNetworkId),
        seasonId: standardProps.seasonId,
        grouping: standardProps.grouping,
        instanceId: "",
        yearOfStudy: "",
        studentType: "",
        pathwayId: "",
      });
    }
  }, [normalize(props.defaultProps), props.isKinetic]);

  const closeModal = () => setIsOpen(false);



  const submitHandler = async (data: IFormData) => {
    setIsLoading(true);

    if (props.isKinetic) {
      const shouldIncludeStudentType = (mappingFields === "BOTH" || mappingFields === "STUDENT_TYPE") && data.studentType;
      const shouldIncludePathway = (mappingFields === "BOTH" || mappingFields === "PATHWAY_ID") && data.pathwayId;

      if (props.defaultProps) {
        // Update existing Kinetic integration
        const res = await updateIntegrationsKinetic(
          selectedNetworkId,
          props.defaultProps.id,
          {
            id: Number(props.defaultProps.id),
            enableAccess: data.access === "enabled",
            instanceId: data.instanceId,
            yearOfStudyId: data.yearOfStudy,
            networkMappingType: data.networkMappingType,
            importFieldSelection: mappingFields,
            ...(shouldIncludeStudentType && { studentType: data.studentType }),
            ...(shouldIncludePathway && { pathwayId: Number(data.pathwayId) }),
          },
          await getAuthToken()
        );

        if (res && res.ok) {
          await loadIntegrations(selectedNetworkId);
          showSuccessSnackbar("Kinetic Integration Updated");
          closeModal();
        } else {
          const errorMessage = extractErrorMessage(res);
          showErrorSnackbar(errorMessage);
        }
      } else {
        // Create new Kinetic integration
        const res = await createIntegrationsKinetic(
          selectedNetworkId,
          {
            l1Id: selectedNetworkId,
            l2Id: subnetwork,
            l3Id: unitType,
            instanceId: data.instanceId,
            yearOfStudyId: data.yearOfStudy,
            networkMappingType: data.networkMappingType,
            enableAccess: data.access === "enabled",
            importFieldSelection: mappingFields,
            ...(shouldIncludeStudentType && { studentType: data.studentType }),
            ...(shouldIncludePathway && { pathwayId: Number(data.pathwayId) }),
          },
          await getAuthToken()
        );

        if (res && res.ok) {
          await loadIntegrations(selectedNetworkId);
          showSuccessSnackbar("Kinetic Integration Created");
          closeModal();
        } else {
          const errorMessage = extractErrorMessage(res);
          showErrorSnackbar(errorMessage);
        }
      }
      setIsLoading(false);
      return;
    }

    if (props.defaultProps) {
      const res = await updateIntegrationsMappings(
        selectedNetworkId,
        props.defaultProps.id,
        {
          id: Number(props.defaultProps.id),
          enableAccess: data.access === "enabled",
          mapping: data.mappingValue,
          seasonId: data.seasonId,
          grouping: data.grouping,
          networkMappingType: data.networkMappingType,
        },
        await getAuthToken()
      );

      if (res && res.ok) {
        await loadIntegrations(selectedNetworkId);
        showSuccessSnackbar("Saved Changes");
        closeModal();
      } else {
        const errorMessage = extractErrorMessage(res);
        showErrorSnackbar(errorMessage);
      }
    } else {
      const res = await createIntegrationsMappings(
        selectedNetworkId,
        {
          l1Id: selectedNetworkId,
          externalId: data.externalId,
          enableAccess: data.access === "enabled",
          mapping: data.mappingValue,
          l2Id: subnetwork,
          l3Id: unitType,
          l4Id: roomType,
          seasonId: data.seasonId,
          grouping: data.grouping,
          networkMappingType: data.networkMappingType,
        },
        await getAuthToken()
      );

      if (res && res.ok) {
        await loadIntegrations(selectedNetworkId);
        showSuccessSnackbar("Successfully Created");
        closeModal();
      } else {
        const errorMessage = extractErrorMessage(res);
        showErrorSnackbar(errorMessage);
      }
    }

    setIsLoading(false);
  };

  const actionLabel = props.defaultProps ? "Edit" : "Create";
  const l4Options = getL4Options(selectedNetworkId, subnetwork, unitType);

  return (
    <Modal
      maxWidth="md"
      title={`${actionLabel} Mapping`}
      actions={
        <>
          <Button variant="outlined" onClick={closeModal}>
            Cancel
          </Button>
          <Button color="primary" loading={isLoading} variant="contained" onClick={handleSubmit(submitHandler)}>
            {actionLabel}
          </Button>
        </>
      }
    >
      <Box p={3} width={400}>
        <Grid container spacing={2}>
          {props.isKinetic ? (
            <>
              {/* Kinetic integration fields */}
              <Grid item xs={12}>
                <L2Selector
                  defaultValue={props.defaultProps?.l2Id}
                  isDisabled={!!props.defaultProps}
                  onSelectHandler={setSubnetwork}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <L3Selector
                  defaultValue={props.defaultProps?.l3Id}
                  isDisabled={!!props.defaultProps}
                  l2Selection={props.defaultProps?.l2Id || subnetwork}
                  onSelectHandler={setUnitType}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  defaultValue="Kinetic"
                  name="networkMappingType"
                  render={({ field }) => (
                    <MUISelect label="Network mapping type" {...field} fullWidth>
                      <MenuItem value="Kinetic">Kinetic</MenuItem>
                      <MenuItem value="ACCSFApi">ACCSFApi</MenuItem>
                      <MenuItem value="TenantKey">TenantKey</MenuItem>
                      <MenuItem value="Email">Email</MenuItem>
                    </MUISelect>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="access"
                  control={control}
                  defaultValue="enabled"
                  render={({ field }) => (
                    <MUISelect label="Enabled/Disabled" {...field} fullWidth>
                      <MenuItem value="enabled">Enabled</MenuItem>
                      <MenuItem value="disabled">Disabled</MenuItem>
                    </MUISelect>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="instanceId"
                  rules={{ required: "Instance ID is required" }}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      variant="filled"
                      label="Instance ID"
                      required
                      error={!!errors.instanceId}
                      helperText={errors.instanceId?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="yearOfStudy"
                  rules={{ required: "Year of Study is required" }}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      variant="filled"
                      label="Year Of Study"
                      required
                      error={!!errors.yearOfStudy}
                      helperText={errors.yearOfStudy?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <MUISelect
                  label="Mapping Fields"
                  value={mappingFields}
                  onChange={(e: SelectChangeEvent<string>) => setMappingFields(e.target.value as MappingFieldsOption)}
                  fullWidth
                >
                  <MenuItem value="BOTH">Student Type & Pathway</MenuItem>
                  <MenuItem value="STUDENT_TYPE">Student Type</MenuItem>
                  <MenuItem value="PATHWAY_ID">Pathway</MenuItem>
                  <MenuItem value="NONE">None</MenuItem>
                </MUISelect>
              </Grid>
              {(mappingFields === "BOTH" || mappingFields === "STUDENT_TYPE") && (
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="studentType"
                    render={({ field }) => <TextField fullWidth variant="filled" label="Student Type" {...field} />}
                  />
                </Grid>
              )}
              {(mappingFields === "BOTH" || mappingFields === "PATHWAY_ID") && (
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="pathwayId"
                    render={({ field }) => <TextField fullWidth variant="filled" label="Pathway" type="number" {...field} />}
                  />
                </Grid>
              )}
            </>
          ) : (
            <>
              {/* Standard integration fields */}
              <Grid item xs={12}>
                <L2Selector
                  defaultValue={props.defaultProps?.l2Id}
                  isDisabled={!!props.defaultProps}
                  onSelectHandler={setSubnetwork}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <L3Selector
                  defaultValue={props.defaultProps?.l3Id}
                  isDisabled={!!props.defaultProps}
                  l2Selection={props.defaultProps?.l2Id || subnetwork}
                  onSelectHandler={setUnitType}
                  fullWidth
                />
              </Grid>
              {(!!((props.defaultProps as IIntegrationsMappings)?.l4Id) || !!l4Options?.length) && (
                <Grid item xs={12}>
                  <L4Selector
                    defaultValue={(props.defaultProps as IIntegrationsMappings)?.l4Id}
                    isDisabled={!!props.defaultProps}
                    l3Selection={props.defaultProps?.l3Id || unitType}
                    l2Selection={props.defaultProps?.l2Id || subnetwork}
                    onSelectHandler={setRoomType}
                    fullWidth
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Controller
                  control={control}
                  defaultValue="ACC"
                  name="networkMappingType"
                  render={({ field }) => (
                    <MUISelect label="Network mapping type" {...field}  fullWidth>
                      <MenuItem value="ACCSFApi">ACCSFApi</MenuItem>
                      <MenuItem value="TenantKey">TenantKey</MenuItem>
                      <MenuItem value="Email">Email</MenuItem>
                    </MUISelect>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="access"
                  control={control}
                  defaultValue="enabled"
                  render={({ field }) => (
                    <MUISelect label="Enabled/Disabled" {...field} fullWidth>
                      <MenuItem value="enabled">Enabled</MenuItem>
                      <MenuItem value="disabled">Disabled</MenuItem>
                    </MUISelect>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="mappingValue"
                  render={({ field }) => <TextField fullWidth variant="filled" label="Mapping value" {...field} />}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="externalId"
                  render={({ field }) => (
                    <TextField disabled={!!props.defaultProps} fullWidth variant="filled" label="External Id" {...field} />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="seasonId"
                  render={({ field }) => <TextField fullWidth variant="filled" label="Season Id" {...field} />}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="grouping"
                  render={({ field }) => <TextField fullWidth variant="filled" label="Grouping" {...field} />}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    </Modal>
  );
};
